import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Crown, Dog, Cat, Bird, Fish, Rabbit,
  PawPrint, Palette, Building2, CheckCircle, Loader2, Sparkles,
  Heart, Users, Calendar, ShoppingBag, Camera, BookOpen, Trophy,
  MapPin, Globe, AlertCircle, HelpCircle, Image, FileText, 
  Scale, Mail, ExternalLink, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import Step1BasicInfo from '../components/CreateClub/Step1BasicInfo';
import Step2PetTypes from '../components/CreateClub/Step2PetTypes';
import Step3Location from '../components/CreateClub/Step3Location';
import Step4Branding from '../components/CreateClub/Step4Branding';
import Step5Features from '../components/CreateClub/Step5Features';
import Step6WhyCreate from '../components/CreateClub/Step6WhyCreate';
import Step7Agreements from '../components/CreateClub/Step7Agreements';

const API = process.env.REACT_APP_BACKEND_URL;

import {
  PET_TYPES, COUNTRIES, LANGUAGES, US_STATES, CLUB_TYPES,
  COLOR_PRESETS, INITIAL_FEATURES, WHY_CREATE_OPTIONS
} from '../data/createClubData';

export default function CreateClub() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [existingClubs, setExistingClubs] = useState([]);
  const [showExistingClubs, setShowExistingClubs] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Basics
    name: '',
    slug: '',
    tagline: '',
    motto: '',  // Club motto
    description: '',
    mission: '',
    purpose: '',  // Purpose statement
    
    // Step 2: Pet Types & Club Type
    pet_types: [],
    other_pet_type: '',  // For "other" pet type
    club_type: 'standard',
    
    // Step 3: Location/Region
    country: 'US',
    state: '',
    region: '',  // Optional region name
    default_language: 'en',  // Preferred language
    
    // Step 4: Branding
    primary_color: '#c9762b',
    secondary_color: '#a65d1f',
    accent_color: '#e3c992',
    selectedPreset: 'Classic Brown',
    logo_url: '',
    banner_url: '',
    
    // Step 5: Features
    features: INITIAL_FEATURES.filter(f => f.default).map(f => f.id),
    
    // Step 6: Why Create
    why_create: [],
    why_create_other: '',
    
    // Step 7: Contact & Agreements
    website_url: '',
    email: '',
    allows_free_members: true,
    bylaws_accepted: false,
    service_agreement_accepted: false,
    request_modification: false,
    modification_notes: '',
  });

  const isPremium = user?.membership_tier === 'premium';
  const totalSteps = 7;  // Now 7 steps (added Why step)

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const togglePetType = (petId) => {
    setFormData(prev => ({
      ...prev,
      pet_types: prev.pet_types.includes(petId)
        ? prev.pet_types.filter(p => p !== petId)
        : [...prev.pet_types, petId]
    }));
  };

  // Check for existing clubs with similar pet types in same region
  const checkExistingClubs = useCallback(async () => {
    if (formData.pet_types.length === 0 || !formData.country) return;
    
    setCheckingExisting(true);
    try {
      // Build pet types list including custom "other" type
      const petTypes = formData.pet_types.includes('other') && formData.other_pet_type
        ? [...formData.pet_types.filter(p => p !== 'other'), formData.other_pet_type.toLowerCase()]
        : formData.pet_types;
      
      const response = await axios.get(`${API}/api/clubs/search/similar`, {
        params: {
          pet_types: petTypes.join(','),
          country: formData.country,
          state: formData.state || undefined,
        }
      });
      
      const clubs = response.data.clubs || [];
      setExistingClubs(clubs);
      
      if (clubs.length > 0) {
        setShowExistingClubs(true);
      }
    } catch (error) {
      console.error('Error checking existing clubs:', error);
      // Continue anyway if check fails
    } finally {
      setCheckingExisting(false);
    }
  }, [formData.pet_types, formData.other_pet_type, formData.country, formData.state]);

  // Check for existing clubs when moving from step 3 (location) to step 4
  useEffect(() => {
    if (step === 3 && formData.pet_types.length > 0 && formData.country) {
      checkExistingClubs();
    }
  }, [step, checkExistingClubs, formData.country, formData.pet_types.length]);

  const selectColorPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
      selectedPreset: preset.name
    }));
  };

  const toggleFeature = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const toggleWhyCreate = (reasonId) => {
    setFormData(prev => ({
      ...prev,
      why_create: prev.why_create.includes(reasonId)
        ? prev.why_create.filter(r => r !== reasonId)
        : [...prev.why_create, reasonId]
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.slug && formData.tagline && formData.description;
      case 2:
        // Must have pet types, and if "other" is selected, must have custom type
        const hasValidPetTypes = formData.pet_types.length > 0;
        const hasOtherType = formData.pet_types.includes('other') ? formData.other_pet_type.trim() : true;
        return hasValidPetTypes && hasOtherType;
      case 3:
        return formData.country; // Location is required
      case 4:
        return formData.primary_color; // Branding
      case 5:
        return true; // Features are optional
      case 6:
        // Why create - at least one reason required, and if "other" selected, must have text
        const hasReason = formData.why_create.length > 0;
        const hasOtherReason = formData.why_create.includes('other') ? formData.why_create_other.trim() : true;
        return hasReason && hasOtherReason;
      case 7:
        // Must accept both agreements
        return formData.bylaws_accepted && formData.service_agreement_accepted;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!isPremium) {
      toast.error('Premium membership required to create a club');
      return;
    }

    if (!formData.bylaws_accepted || !formData.service_agreement_accepted) {
      toast.error('You must accept the Club Bylaws and Service Agreement');
      return;
    }

    setLoading(true);
    try {
      // Build feature config
      const feature_config = {};
      INITIAL_FEATURES.forEach(f => {
        feature_config[f.id] = formData.features.includes(f.id);
      });

      // Build pet types list including custom "other" type
      let petTypes = formData.pet_types.filter(p => p !== 'other');
      if (formData.pet_types.includes('other') && formData.other_pet_type) {
        petTypes.push(formData.other_pet_type.toLowerCase());
      }

      const now = new Date().toISOString();

      const response = await axios.post(`${API}/api/clubs/create`, {
        name: formData.name,
        slug: formData.slug,
        tagline: formData.tagline,
        motto: formData.motto || null,
        description: formData.description,
        mission: formData.mission || `Building a community for ${petTypes.join(', ')} lovers.`,
        purpose: formData.purpose || null,
        pet_types: petTypes,
        club_type: formData.club_type,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        accent_color: formData.accent_color,
        logo_url: formData.logo_url || null,
        banner_url: formData.banner_url || null,
        website_url: formData.website_url || null,
        email: formData.email || null,
        country: formData.country,
        state: formData.state || null,
        region: formData.region || null,
        default_language: formData.default_language,
        available_countries: [formData.country],
        allows_free_members: formData.allows_free_members,
        feature_config: feature_config,
        // Why create fields
        why_create: formData.why_create,
        why_create_other: formData.why_create.includes('other') ? formData.why_create_other : null,
        // Agreement fields
        bylaws_accepted: formData.bylaws_accepted,
        bylaws_accepted_at: now,
        service_agreement_accepted: formData.service_agreement_accepted,
        service_agreement_accepted_at: now,
        agreement_modification_requested: formData.request_modification,
        agreement_modification_notes: formData.request_modification ? formData.modification_notes : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Club created successfully!');
      navigate(`/creator-dashboard`);
    } catch (error) {
      console.error('Error creating club:', error);
      toast.error(error.response?.data?.detail || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  // Premium required check
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-muted/30 py-12" data-testid="create-club-upgrade">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Crown className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-3xl font-heading font-medium mb-4">Premium Feature</h1>
          <p className="text-muted-foreground mb-8">
            Club creation is an exclusive Premium feature. Upgrade to Premium to create 
            and manage your own pet community clubs.
          </p>
          <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
            <Link to="/membership/upgrade">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="create-club-page">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/creator-dashboard" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Creator Dashboard
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-heading font-medium">Create Your Club</h1>
          </div>
          <p className="text-muted-foreground">
            Set up your new pet community in a few simple steps.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span className="text-muted-foreground">
              {step === 1 && 'Basic Info'}
              {step === 2 && 'Pet Types'}
              {step === 3 && 'Location'}
              {step === 4 && 'Branding'}
              {step === 5 && 'Features'}
              {step === 6 && 'Why Create'}
              {step === 7 && 'Agreements'}
            </span>
          </div>
          <Progress value={(step / totalSteps) * 100} className="h-2" />
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && <Step1BasicInfo formData={formData} setFormData={setFormData} generateSlug={generateSlug} />}

        {/* Step 2: Pet Types & Club Type */}
        {step === 2 && <Step2PetTypes formData={formData} setFormData={setFormData} />}

        {/* Step 3: Location / Region */}
        {step === 3 && <Step3Location formData={formData} setFormData={setFormData} existingClubs={existingClubs} showExistingClubs={showExistingClubs} setShowExistingClubs={setShowExistingClubs} checkingExisting={checkingExisting} />}

        {/* Step 4: Branding */}
        {step === 4 && <Step4Branding formData={formData} setFormData={setFormData} />}

        {/* Step 5: Features */}
        {step === 5 && <Step5Features formData={formData} />}

        {/* Step 6: Why Create This Club */}
        {step === 6 && <Step6WhyCreate formData={formData} setFormData={setFormData} />}

        {/* Step 7: Agreements & Final Settings */}
        {step === 7 && <Step7Agreements formData={formData} setFormData={setFormData} agreements={agreements} setAgreements={setAgreements} />}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className="bg-primary"
              data-testid="create-club-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Club
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
